import React, { createContext, useContext, useEffect, useState } from 'react';
import { Website } from './WebsitesContext';
import { supabase } from '@/integrations/supabase/client';

interface Organisation {
  id: string;
  name: string;
  created_at: string;
  current_plan?: string;
  credits?: number;
  next_payment_date?: string;
}

interface WebsiteSelectionContextType {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  selectedWebsite: Website | null;
  setSelectedWebsite: (website: Website | null) => void;
  resetSelectedWebsite: () => void;
  // New state for organisations and websites
  organisations: Organisation[];
  websites: Website[];
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedOrganisation: string | null;
  setSelectedOrganisation: (orgId: string | null) => void;
  // New methods
  fetchOrganisationsAndWebsites: () => Promise<void>;
  handleWebsiteSelect: (website: Website) => void;
  handleOrganisationSelect: (orgId: string) => void;
  // Computed values
  groupedWebsites: Record<string, Website[]>;
  selectedOrganisationWebsites: Website[];
  filteredOrganisations: Organisation[];
  filteredSelectedOrganisationWebsites: Website[];
}

// Local storage keys
const STORAGE_KEYS = {
  SELECTED_ORGANISATION: 'wpcontentai_selected_organisation',
  SELECTED_WEBSITE: 'wpcontentai_selected_website'
} as const;

const WebsiteSelectionContext = createContext<WebsiteSelectionContextType | undefined>(undefined);

export const WebsiteSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState<Website | null>(null);
  
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState<string | null>(null);

  // Local storage utilities
  const saveToLocalStorage = (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage (${key}):`, error);
    }
  };

  const getFromLocalStorage = (key: string) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return null;
    }
  };

  // Enhanced setSelectedWebsite with local storage
  const setSelectedWebsiteWithStorage = (website: Website | null) => {
    setSelectedWebsite(website);
    if (website) {
      saveToLocalStorage(STORAGE_KEYS.SELECTED_WEBSITE, website);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_WEBSITE);
    }
  };

  // Enhanced setSelectedOrganisation with local storage
  const setSelectedOrganisationWithStorage = (orgId: string | null) => {
    setSelectedOrganisation(orgId);
    if (orgId) {
      saveToLocalStorage(STORAGE_KEYS.SELECTED_ORGANISATION, orgId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_ORGANISATION);
    }
  };

  const groupedWebsites = React.useMemo(() => {
    return websites.reduce((acc, website) => {
      if (!acc[website.organisation_id]) {
        acc[website.organisation_id] = [];
      }
      acc[website.organisation_id].push(website);
      return acc;
    }, {} as Record<string, Website[]>);
  }, [websites]);

  const selectedOrganisationWebsites = React.useMemo(() => {
    return selectedOrganisation
      ? websites.filter((website) => website.organisation_id === selectedOrganisation)
      : [];
  }, [selectedOrganisation, websites]);

  const filteredOrganisations = React.useMemo(() => {
    return organisations.filter((org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [organisations, searchTerm]);

  const filteredSelectedOrganisationWebsites = React.useMemo(() => {
    if (!selectedOrganisation) return [];
    if (searchTerm === "") return selectedOrganisationWebsites;
    return selectedOrganisationWebsites.filter(
      (website) =>
        website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        website.url.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [selectedOrganisation, searchTerm, selectedOrganisationWebsites]);

  const fetchOrganisationsAndWebsites = async () => {
    setIsLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: memberships } = await supabase
        .from("organisation_memberships")
        .select(
          `
          organisation_id,
          role,
          organisations (
            id,
            name,
            created_at,
            current_plan,
            credits,
            next_payment_date
          )
        `
        )
        .eq("member_id", user.id);

      if (memberships) {
        const orgs = memberships
          .map((m) => m.organisations)
          .filter(Boolean) as Organisation[];
        setOrganisations(orgs);

        const orgIds = orgs.map((org) => org.id);
        const { data: websitesData } = await supabase
          .from("websites")
          .select("*")
          .in("organisation_id", orgIds);

        if (websitesData) {
          const websitesWithOrg = websitesData.map((website) => ({
            ...website,
            organisation_name: orgs.find((org) => org.id === website.organisation_id)
              ?.name,
          }));
          setWebsites(websitesWithOrg);

          // Load from local storage or set defaults
          await loadSelectedOrganisationAndWebsite(orgs, websitesWithOrg);
        }
      }
    } catch (error) {
      console.error("Error fetching organisations and websites:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSelectedOrganisationAndWebsite = async (orgs: Organisation[], websitesWithOrg: Website[]) => {
    if (orgs.length === 0 || websitesWithOrg.length === 0) return;

    // Try to load from local storage - check both new and old systems
    const storedOrgId = getFromLocalStorage(STORAGE_KEYS.SELECTED_ORGANISATION);
    const storedWebsite = getFromLocalStorage(STORAGE_KEYS.SELECTED_WEBSITE);
    
    // Also check the old localStorage system
    const oldWebsiteId = localStorage.getItem('currentWebsiteId');
    const oldOrgId = localStorage.getItem('currentOrgId');

    let orgToSelect: string;
    let websiteToSelect: Website | null = null;

    // Priority: Check if old system has a valid website ID that exists in our data
    if (oldWebsiteId && oldOrgId) {
      const oldWebsite = websitesWithOrg.find(website => website.id === oldWebsiteId);
      if (oldWebsite && oldWebsite.organisation_id === oldOrgId) {
        orgToSelect = oldOrgId;
        websiteToSelect = oldWebsite;
        console.log("WebsiteSelectionProvider: Using old localStorage system - found valid website:", oldWebsite.name);
      } else {
        // The stored website ID doesn't exist in our data, clear it
        console.log("WebsiteSelectionProvider: Clearing invalid website ID from localStorage:", oldWebsiteId);
        localStorage.removeItem('currentWebsiteId');
        localStorage.removeItem('currentWebsiteName');
        localStorage.removeItem('currentWebsiteUrl');
        localStorage.removeItem('currentOrgId');
        localStorage.removeItem('current_organisation_id');
        localStorage.removeItem('currentWebsiteData');
      }
    }

    // Fallback: Check new system
    if (!websiteToSelect && storedOrgId && orgs.find(org => org.id === storedOrgId)) {
      const orgWebsites = websitesWithOrg.filter(website => website.organisation_id === storedOrgId);
      if (orgWebsites.length > 0) {
        orgToSelect = storedOrgId;
        
        // Check if stored website still exists in this organisation
        if (storedWebsite && orgWebsites.find(website => website.id === storedWebsite.id)) {
          websiteToSelect = storedWebsite;
        } else {
          // Use first website from this organisation
          websiteToSelect = orgWebsites[0];
        }
      } else {
        // Organisation exists but has no websites, fall back to first available
        orgToSelect = orgs[0].id;
        const firstOrgWebsites = websitesWithOrg.filter(website => website.organisation_id === orgToSelect);
        websiteToSelect = firstOrgWebsites.length > 0 ? firstOrgWebsites[0] : null;
      }
    } else if (!websiteToSelect) {
      // Stored organisation doesn't exist or no stored data, use first available
      orgToSelect = orgs[0].id;
      const firstOrgWebsites = websitesWithOrg.filter(website => website.organisation_id === orgToSelect);
      websiteToSelect = firstOrgWebsites.length > 0 ? firstOrgWebsites[0] : null;
    }

    // Set the selected organisation and website
    setSelectedOrganisationWithStorage(orgToSelect);
    if (websiteToSelect) {
      setSelectedWebsiteWithStorage(websiteToSelect);
      
      // Also sync with the old localStorage system to ensure consistency
      localStorage.setItem('currentWebsiteId', websiteToSelect.id);
      localStorage.setItem('currentWebsiteName', websiteToSelect.name);
      localStorage.setItem('currentWebsiteUrl', websiteToSelect.url);
      localStorage.setItem('currentOrgId', websiteToSelect.organisation_id);
      localStorage.setItem('current_organisation_id', websiteToSelect.organisation_id);
      
      console.log("WebsiteSelectionProvider: Synced with old localStorage system:", {
        id: websiteToSelect.id,
        name: websiteToSelect.name,
        organisation_id: websiteToSelect.organisation_id
      });
    }
  };

  const handleWebsiteSelect = (website: Website) => {
    // Update both localStorage systems to ensure consistency
    
    // Update the old localStorage system (the one that actually works)
    localStorage.setItem('currentWebsiteId', website.id);
    localStorage.setItem('currentWebsiteName', website.name);
    localStorage.setItem('currentWebsiteUrl', website.url);
    localStorage.setItem('currentOrgId', website.organisation_id);
    localStorage.setItem('current_organisation_id', website.organisation_id);
    
    // Also update organization data for the old system
    const organisation = organisations.find(org => org.id === website.organisation_id);
    if (organisation) {
      localStorage.setItem('currentOrganisation', JSON.stringify(organisation));
    }
    
    // Store additional website data for debugging and compatibility
    localStorage.setItem('currentWebsiteData', JSON.stringify({
      id: website.id,
      name: website.name,
      url: website.url,
      organisation_id: website.organisation_id,
      timestamp: new Date().toISOString()
    }));
    
    // Update the new localStorage system as well
    setSelectedWebsiteWithStorage(website);
    setSelectedOrganisationWithStorage(website.organisation_id);
    
    console.log("WebsiteSelectionProvider: Updated both localStorage systems with website:", {
      id: website.id,
      name: website.name,
      url: website.url,
      organisation_id: website.organisation_id
    });
    
    // Close modal and navigate to dashboard for the selected website
    setIsModalOpen(false);
    // Navigate to dashboard - the app will use the selected website context
    window.location.href = '/dashboard';
  };

  const handleOrganisationSelect = (orgId: string) => {
    setSelectedOrganisationWithStorage(orgId);
    
    // Find first website in the selected organisation
    const orgWebsites = websites.filter(website => website.organisation_id === orgId);
    if (orgWebsites.length > 0) {
      setSelectedWebsiteWithStorage(orgWebsites[0]);
    } else {
      setSelectedWebsiteWithStorage(null);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const resetSelectedWebsite = () => {
    setSelectedWebsiteWithStorage(null);
  };

  // Set selected organisation when selectedWebsite changes
  useEffect(() => {
    if (selectedWebsite && websites.length > 0) {
      const currentOrgId = selectedWebsite.organisation_id;
      if (currentOrgId && currentOrgId !== selectedOrganisation) {
        setSelectedOrganisationWithStorage(currentOrgId);
      }
    }
  }, [selectedWebsite, websites]);

  useEffect(() => {
    fetchOrganisationsAndWebsites();
  }, []);

  useEffect(() => {
    if (selectedWebsite) {
      console.log("WebsiteSelectionProvider: 🌺 Selected website:", selectedWebsite);
    }
  }, [selectedWebsite]);

  useEffect(() => {
    if (selectedOrganisation) {
      console.log("WebsiteSelectionProvider: Selected organisation:", selectedOrganisation);
    } else {
      console.log("WebsiteSelectionProvider: No organisation selected");
    }

    if (websites && websites.length > 0) {
      console.log("WebsiteSelectionProvider: All websites:", websites);
    } else {
      console.log("WebsiteSelectionProvider: No websites found");
    }

    if (selectedOrganisationWebsites && selectedOrganisationWebsites.length > 0) {
      console.log(
        "WebsiteSelectionProvider: Selected organisation websites:",
        selectedOrganisationWebsites
      );
    } else {
      console.log("WebsiteSelectionProvider: No websites found for selected organisation");
    }
  }, [selectedOrganisation, websites, selectedOrganisationWebsites]);

  return (
    <WebsiteSelectionContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,
        selectedWebsite,
        setSelectedWebsite: setSelectedWebsiteWithStorage,
        resetSelectedWebsite,
        // New state and methods
        organisations,
        websites,
        isLoading,
        searchTerm,
        setSearchTerm,
        selectedOrganisation,
        setSelectedOrganisation: setSelectedOrganisationWithStorage,
        fetchOrganisationsAndWebsites,
        handleWebsiteSelect,
        handleOrganisationSelect,
        // Computed values
        groupedWebsites,
        selectedOrganisationWebsites,
        filteredOrganisations,
        filteredSelectedOrganisationWebsites,
      }}
    >
      {children}
    </WebsiteSelectionContext.Provider>
  );
};

export const useWebsiteSelection = () => {
  const context = useContext(WebsiteSelectionContext);
  if (context === undefined) {
    throw new Error('useWebsiteSelection must be used within a WebsiteSelectionProvider');
  }
  return context;
};
